"use client";

import { Button } from "@heroui/button";
import { Input } from "@heroui/input";
import { Link } from "@heroui/link";
import { Alert02Icon, ViewIcon, ViewOffSlashIcon } from "@icons";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { type FormEvent, useState } from "react";
import { organizationApi } from "@/features/organizations/api/organizationApi";
import { extractApiError } from "@/lib/api/extractError";
import { authApi } from "../api/authApi";
import { MIN_PASSWORD_LENGTH } from "../constants";

interface AuthFormProps {
  mode: "login" | "signup";
}

// Only accept relative in-app paths (rejects //evil.com and /\evil.com).
function safeReturnUrl(raw: string | null): string {
  return raw && /^\/[^/\\]/.test(raw) ? raw : "/c";
}

export function AuthForm({ mode }: AuthFormProps) {
  const $t = useTranslations();
  const isLogin = mode === "login";
  const searchParams = useSearchParams();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (isLogin) {
        await authApi.loginWithPassword(email.trim(), password);
      } else {
        await authApi.signupWithPassword(name.trim(), email.trim(), password);
        // Public sign-up provisions the new user's Discovery workspace (the
        // backend no-ops if they already have one or were invited). The guard
        // then routes them through onboarding → workspace setup wizard.
        await organizationApi.provisionSelfServe().catch(() => {});
      }
      // Full reload so middleware + the user fetch run with the fresh cookie.
      window.location.assign(safeReturnUrl(searchParams.get("return_url")));
    } catch (err) {
      setError(
        extractApiError(
          err,
          isLogin
            ? "Invalid email or password."
            : "Could not create your account.",
        ),
      );
      setSubmitting(false);
    }
  };

  const canSubmit =
    !!email.trim() &&
    password.length >= (isLogin ? 1 : MIN_PASSWORD_LENGTH) &&
    (isLogin || !!name.trim());

  // Carry the return destination across the login <-> signup toggle.
  const returnParam = searchParams.get("return_url");
  const toggleQuery = returnParam
    ? `?return_url=${encodeURIComponent(returnParam)}`
    : "";

  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-center px-4">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-40"
        style={{
          backgroundImage: "url('/images/wallpapers/bands_gradient_black.png')",
        }}
      />

      <div className="relative z-10 w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <Image
            src="/images/logos/text_w_logo_white.webp"
            alt={$t("auth.aikodo")}
            width={120}
            height={36}
            priority
          />
        </div>

        <div className="rounded-3xl border border-white/10 bg-content1/70 p-6 shadow-2xl backdrop-blur-xl">
          <h1 className="mb-1 text-xl font-semibold text-foreground">
            {isLogin ? "Sign in" : "Create your account"}
          </h1>
          <p className="mb-5 text-sm text-default-500">
            {isLogin
              ? "Welcome back. Enter your details to continue."
              : "Set up an account with your email and a password."}
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            {!isLogin && (
              <Input
                label="Name"
                labelPlacement="outside"
                placeholder={$t("auth.your_name")}
                autoComplete="name"
                value={name}
                onValueChange={setName}
                isDisabled={submitting}
              />
            )}
            <Input
              type="email"
              label="Email"
              labelPlacement="outside"
              placeholder="you@company.com"
              autoComplete="email"
              value={email}
              onValueChange={setEmail}
              isDisabled={submitting}
            />
            <Input
              type={showPassword ? "text" : "password"}
              label="Password"
              labelPlacement="outside"
              placeholder={
                isLogin
                  ? "Your password"
                  : `At least ${MIN_PASSWORD_LENGTH} characters`
              }
              autoComplete={isLogin ? "current-password" : "new-password"}
              value={password}
              onValueChange={setPassword}
              isDisabled={submitting}
              endContent={
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-default-500 transition hover:text-default-700"
                >
                  {showPassword ? (
                    <ViewOffSlashIcon className="size-4" />
                  ) : (
                    <ViewIcon className="size-4" />
                  )}
                </button>
              }
            />

            {isLogin && (
              <div className="-mt-1 flex justify-end">
                <Link
                  href="/forgot-password"
                  size="sm"
                  className="text-default-500"
                >
                  Forgot password?
                </Link>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-xl bg-danger/10 px-3 py-2 text-sm text-danger">
                <Alert02Icon className="mt-0.5 size-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              color="primary"
              className="mt-1 font-medium"
              isLoading={submitting}
              isDisabled={!canSubmit || submitting}
            >
              {isLogin ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="mt-5 text-center text-sm text-default-500">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <Link
              href={`${isLogin ? "/signup" : "/login"}${toggleQuery}`}
              size="sm"
              className="text-primary"
            >
              {isLogin ? "Sign up" : "Sign in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
